import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import * as Sentry from '@sentry/browser';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlobalToken } from 'antd';

import { NavBack, UserCreateOrEditForm } from '@/comp';
import { useStores } from '@/hooks/use-stores';
import { User } from '@/store/UsersStore';

const UserEditPage = observer(() => {
  const { t } = useTranslation();
  const { usersStore, associableEntitiesStore } = useStores();
  const params = useParams();
  const [user, setUser] = useState({ fullName: null, lastUpdated: null } as User);
  const [isReady, setIsReady] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const onChangeDirty = value => {
    setIsDirty(value);
  };

  useEffect(() => {
    // fetch associableEntitiesStore in case never fetched before
    !associableEntitiesStore.isReady && associableEntitiesStore.get();
  }, [associableEntitiesStore.isReady]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const newUser = await usersStore.getById(params.userId);
        setUser(newUser ?? { fullName: null, lastUpdated: null });
      } catch (e) {
        Sentry.captureException(new Error(`Error when fetching user: ${e}`));
      }
    };

    // fetch user by id from store
    fetchUser();
  }, [params.userId]);

  useEffect(() => {
    if (!!user && (associableEntitiesStore.isReady || associableEntitiesStore.isPartiallyReady)) setIsReady(true);
  }, [user, associableEntitiesStore.isReady, associableEntitiesStore.isPartiallyReady]);

  return (
    <Container>
      <NavBack
        url={`/home/station/${params.stationId}/settings/users`}
        popConfirmProps={
          isDirty
            ? {
                icon: null,
                placement: 'bottomRight',
                title: t('Changes will not be saved. Are you sure?'),
                okText: t('Discard changes'),
                cancelText: t('Dismiss'),
              }
            : undefined
        }
      />
      <div className="form-container">
        <UserCreateOrEditForm user={user} onChangeDirty={onChangeDirty} isReady={isReady && !usersStore.isLoading} />
      </div>
    </Container>
  );
});

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  flex-grow: 1;
  overflow-y: auto;
  padding: ${({ theme: { token } }) => token.paddingMD}px;
  display: flex;
  flex-direction: column;

  .form-container {
    margin-top: ${({ theme: { token } }) => token.marginLG}px;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
`;

export default UserEditPage;
